"use client"

import { t } from "@/languages/i18n"
import { useEffect, useRef } from "react"
import { LatestNewsArticle } from "../types"
import FeaturedArticle from "./FeaturedArticle"
import LatestNewsPagination from "./LatestNewsPagination"
import LatestNewsSkeleton from "./LatestNewsSkeleton"
import NewsGridItem from "./NewsGridItem"
import SidebarArticle from "./SidebarArticle"

interface LatestNewsContentProps {
    articles?: LatestNewsArticle[]
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    loading?: boolean
    title?: string
}

const LatestNewsContent = ({
    articles,
    currentPage,
    totalPages,
    onPageChange,
    loading,
    title,
}: LatestNewsContentProps) => {
    const hasEverLoaded = useRef(false)

    useEffect(() => {
        if (loading === false) {
            hasEverLoaded.current = true
        }
    }, [loading])

    if (loading || articles === undefined) {
        return <LatestNewsSkeleton />
    }

    if (articles.length === 0) {
        return (
            <div className="w-full flex items-center justify-center py-20">
                <p className="text-[14px] leading-[21.7px] text-[#333]">{t("No articles available")}</p>
            </div>
        )
    }

    const featuredArticle = articles[0]
    const sidebarArticle = articles[1]
    const gridArticles = articles.slice(2, 5)

    return (
        <div className="w-full flex flex-col gap-5">
            <h2 className="sm:text-2xl sm:leading-[37.2px] text-lg leading-[27.9px] font-bold text-[#333] uppercase">{title}</h2>

            <div className="flex flex-col sm:flex-row gap-5">
                {featuredArticle && (
                    <div className="w-full sm:w-2/3">
                        <FeaturedArticle article={featuredArticle} />
                    </div>
                )}

                {sidebarArticle && (
                    <div className="hidden sm:block w-full sm:w-1/3">
                        <SidebarArticle article={sidebarArticle} />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                {sidebarArticle && (
                    <div className="sm:hidden">
                        <SidebarArticle article={sidebarArticle} />
                    </div>
                )}
                {gridArticles.map((article) => (
                    <NewsGridItem key={article.id} article={article} />
                ))}
            </div>

            {totalPages > 1 && (
                <div>
                    <LatestNewsPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                        loading={loading}
                    />
                </div>
            )}
        </div>
    )
}

export default LatestNewsContent
